import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api'; // @@SSO_MFA_v126@@
import './clientPortal.css';

// @@CLIENT_PORTAL_v1@@
// Standalone client-portal shell — deliberately NOT the admin Layout (no admin
// nav, no internal counts). Clients only ever see this. The client switcher is
// rendered by the pages (they own the selected client), so the shell is just
// the frame + sign-out.
// @@PORTAL_MOBILE_v91@@ Mobile nav + stacked tables.
// Keep MOBILE_NAV_MQ in sync with the hamburger breakpoint in clientPortal.css.
const MOBILE_NAV_MQ = '(max-width: 1100px)';
const TAB_LABELS = {
  dashboard: 'Dashboard', statement: 'Account Statement', invoices: 'Invoices',
  estimates: 'Estimates', // @@PORTAL_ESTIMATES_v93@@
  techpacks: 'Tech Packs', // @@TECH_PACKS_v94@@
  inventory: 'Inventory', shipping: 'Shipping', equipment: 'Equipment',
  quickquote: 'Quick Quote', payment: 'Payment Info',
};

// Copy each .cp-table header text onto its body cells as data-label so the
// phone CSS can render rows as label/value cards. Purely presentational:
// React never sets data-label, so writing it can't fight reconciliation, and
// setAttribute doesn't trigger the childList/characterData observer (no loop).
function labelTables(root) {
  root.querySelectorAll('table.cp-table').forEach((tbl) => {
    if (tbl.classList.contains('pi-kv')) return; // key/value tables stay as-is
    const heads = Array.from(tbl.querySelectorAll('thead th'))
      .map((th) => (th.textContent || '').replace(/[▲▼]/g, '').trim());
    if (!heads.length) return;
    tbl.querySelectorAll('tbody tr').forEach((tr) => {
      let col = 0;
      Array.from(tr.children).forEach((td) => {
        const span = td.colSpan || 1;
        const label = span === 1 ? (heads[col] || '') : '';
        if (td.getAttribute('data-label') !== label) td.setAttribute('data-label', label);
        col += span;
      });
    });
  });
}
function useTableLabels(ref) {
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;
    let raf = 0;
    const run = () => { raf = 0; labelTables(root); };
    run();
    const mo = new MutationObserver(() => { if (!raf) raf = window.requestAnimationFrame(run); });
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); if (raf) window.cancelAnimationFrame(raf); };
  }, [ref]);
}

export default function ClientPortalLayout({ children, tab, onTab, shipping, equipment /* @@EQUIPMENT_LEASES_v87@@ */, techpacks /* @@TECH_PACKS_v94@@ */, inventory = true /* @@REPORT_REVIEW_v115@@ */, onPay /* @@PORTAL_PAY_v124_1@@ header "Make a Payment" */, payment = true /* @@PAYMENT_ACCESS_v125@@ */ }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  function handleLogout() { logout(); navigate('/login'); }
  // @@SSO_MFA_v126@@ connect a Google account so future sign-ins skip the emailed code.
  const [googleOn, setGoogleOn] = useState(false);
  useEffect(() => {
    fetch('/api/auth/config').then((r) => (r.ok ? r.json() : null)).then((d) => setGoogleOn(!!(d && d.google))).catch(() => {});
  }, []);
  const googleQ = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('google') : null;
  async function connectGoogle() {
    try {
      const res = await apiFetch('/api/auth/google/link-token', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.url) { window.alert(d.error || 'Could not start Google connection.'); return; }
      window.location.assign(d.url);
    } catch (e) { window.alert('Could not reach the server.'); }
  }
  const showConnect = googleOn && user && !user.google_linked;
  const name = user ? (`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email) : '';

  // @@PORTAL_MOBILE_v91@@ hamburger state
  const [menuOpen, setMenuOpen] = useState(false);
  const mainRef = useRef(null);
  useTableLabels(mainRef);
  const current = TAB_LABELS[tab] || '';
  function go(t) {
    setMenuOpen(false);
    if (onTab) onTab(t);
    if (window.matchMedia && window.matchMedia(MOBILE_NAV_MQ).matches) window.scrollTo(0, 0);
  }
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const mq = window.matchMedia ? window.matchMedia(MOBILE_NAV_MQ) : null;
    const onMq = () => { if (mq && !mq.matches) setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    if (mq && mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq && mq.addListener) mq.addListener(onMq);
    document.body.classList.add('cp-noscroll');
    return () => {
      document.removeEventListener('keydown', onKey);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onMq);
      else if (mq && mq.removeListener) mq.removeListener(onMq);
      document.body.classList.remove('cp-noscroll');
    };
  }, [menuOpen]);

  return (
    <div className="cp-app">
      <header className={onPay ? 'cp-top cp-top-pay' : 'cp-top'}>
        <div className="cp-brand">
          <img src="/brand/xyz-mark.png" alt="XYZ Merchandise" />
          <div className="cp-wm">XYZ<span>Client Portal</span></div>
        </div>
        {/* @@PORTAL_MOBILE_v91@@ current tab + hamburger (CSS shows them <=1100px) */}
        {current && <div className="cp-cur">{current}</div>}
        {/* @@PORTAL_PAY_v124_1@@ green "Make a Payment" (opens the pay dialog on the Invoices tab) */}
        {onPay && (
          <button type="button" className="cp-pay-cta" onClick={() => { setMenuOpen(false); onPay(); }}>
            <span className="cp-pay-cta-long">Make a Payment</span><span className="cp-pay-cta-short">Pay</span>
          </button>
        )}
        <button type="button" className={menuOpen ? 'cp-burger cp-burger-on' : 'cp-burger'}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="cp-nav"
          onClick={() => setMenuOpen((o) => !o)}>
          <span /><span /><span />
        </button>
        <nav id="cp-nav" className={menuOpen ? 'cp-tabs cp-tabs-open' : 'cp-tabs'}>
          <button className={tab === 'dashboard' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('dashboard')}>Dashboard</button>
          <button className={tab === 'statement' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('statement')}>Account Statement</button>
          {/* @@PORTAL_INVOICES_v83@@ always visible */}
          <button className={tab === 'invoices' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('invoices')}>Invoices</button>
          {/* @@PORTAL_ESTIMATES_v93@@ always visible */}
          <button className={tab === 'estimates' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('estimates')}>Estimates</button>
          {/* @@TECH_PACKS_v94@@ shown when the account has a tech pack */}
          {techpacks && <button className={tab === 'techpacks' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('techpacks')}>Tech Packs</button>}
          {/* @@INVENTORY_NAV_v1@@ */}
          {inventory && <button className={tab === 'inventory' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('inventory')}>Inventory</button>}{/* @@REPORT_REVIEW_v115@@ hidden when no Shopify store */}
          {/* @@SHIPPING_NAV_v2@@ */}
          {shipping && <button className={tab === 'shipping' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('shipping')}>Shipping</button>}
          {/* @@EQUIPMENT_LEASES_v87@@ shown when the account has a lease */}
          {equipment && <button className={tab === 'equipment' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('equipment')}>Equipment</button>}
          {/* @@QUICKQUOTE_NAV_v1@@ always visible */}
          <button className={tab === 'quickquote' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('quickquote')}>Quick Quote</button>
          {/* @@PAYMENT_INFO_v82@@ shown unless the back office marked Payment info not needed for this client (@@PAYMENT_ACCESS_v125@@) */}
          {payment && <button className={tab === 'payment' ? 'cp-tab cp-active' : 'cp-tab'}
            onClick={() => go('payment')}>Payment Info</button>}
          {/* @@PORTAL_MOBILE_v91@@ name + sign out live in the sheet on mobile */}
          <div className="cp-drawer-foot">
            <span className="cp-drawer-name">{name}</span>
            {showConnect && <button type="button" className="cp-signout" onClick={connectGoogle}>Connect Google</button>}{/* @@SSO_MFA_v126@@ */}
            <button type="button" className="cp-signout" onClick={handleLogout}>Sign out</button>
          </div>
        </nav>
        <div className="cp-who">
          <span className="cp-name">{name}</span>
          {showConnect && <button className="cp-signout" onClick={connectGoogle}>Connect Google</button>}{/* @@SSO_MFA_v126@@ */}
          <button className="cp-signout" onClick={handleLogout}>Sign out</button>
        </div>
      </header>
      {menuOpen && <div className="cp-backdrop" aria-hidden="true" onClick={() => setMenuOpen(false)} />}
      <main className="cp-main" ref={mainRef}>
        {/* @@SSO_MFA_v126@@ */}
        {googleQ === 'linked' && <div className="cp-alert" style={{ marginBottom: 20 }}><strong>Google connected.</strong> Next time, use “Continue with Google” on the sign-in page — no emailed code needed.</div>}
        {googleQ === 'taken' && <div className="cp-alert" style={{ marginBottom: 20 }}><strong>That Google account is already connected to another user.</strong> Try a different Google account, or contact XYZ.</div>}
        {children}
      </main>
    </div>
  );
}
